import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import classNames from "classnames";
import { uuidv7 } from "uuidv7";
import {
    DocumentSource,
    MAX_FINANCIAL_DOCUMENT_FILE_SIZE_BYTES,
    MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB,
} from "@common/constants/financial-documents";
import { FiltersTypes } from "@common/types/filters/general";
import {
    FiltersChangeFn,
    FilterTabs,
} from "@components/pageHeader/filters/lib";
import { formatExtensionsForDropzone } from "@common/helpers/financialDocument";
import { FetchFinancialDocumentsResponse } from "@common/dto/financialDocument/fetch-financial-documents-response.dto";
import {
    AccrualReviewStatus,
    TransactionsImportStatus,
} from "@common/types/financialDocument";
import { useScope } from "@state/scopeContext.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useFinancialDocumentsExtendedQuery } from "@components/documents/useFinancialDocumentsExtendedQuery";
import { accrualImporterDetailIncludes } from "@components/documents/financialDocumentDetailIncludes";
import { invalidateInvoiceQueries } from "@api/invoice.api";
import { invalidateBillQueries } from "@api/bill.api";
import { ImporterToast } from "@components/importer/ImporterToast";
import { IMPORTER_TOAST_KEY } from "@components/importer/ImporterToast.lib";
import { isImportRunning } from "@components/importer/helpers";
import { FileDropzone } from "@design-system/FileDropzone/FileDropzone";
import { VStack } from "@design-system/VStack/VStack";
import { UploadIcon } from "@icons";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { Picker } from "@design-system/Picker/Picker.lib";
import { AccrualImporterGroupBy } from "@common/types/views/accrualImporterViews";
import { useAccrualImporterPageView } from "./useAccrualImporterPageView";
import {
    ACCRUAL_DOCUMENT_FILE_TYPES,
    ACCRUAL_DOCUMENT_FILE_TYPES_NAMES,
} from "./accrualDocumentImporter.lib";
import { AccrualImporterContext } from "./accrualImporter.context";
import {
    accrualDocumentBeingProcessed,
    isAccrualRowReadyForImport,
} from "./accrualImporter.helpers";
import { useAccrualImporterUploadMutation } from "./useAccrualImporterUpload";
import { AccrualImporterPageFooter } from "./AccrualImporterPageFooter";
import { AccrualImporterReviewButton } from "./AccrualImporterReviewButton";
import { useConfirmAccrualDocuments } from "./useConfirmAccrualDocuments";
import { AccrualDraftDetailsDrawer } from "./draftDetails/AccrualDraftDetailsDrawer";
import { accrualDraftCopy } from "./draftDetails/accrualDraftCopy";
import {
    AccrualImporterDocumentRow,
    AccrualImporterViewRow,
} from "./accrualImporter.types";
import { accrualDraftSourceKey } from "./accrualImporterDraftFilters";
import { AccrualDraftCreateMenu } from "./AccrualDraftCreateMenu";
import { AccrualImporterDraftsTable } from "./AccrualImporterDraftsTable";
import { ForwardViaEmailModal } from "./ForwardViaEmailModal";
import styles from "./AccrualImporterDraftsView.module.scss";

const ACCRUAL_DRAFT_FILTERS = [
    FilterTabs.DRAFT_SOURCE,
    FilterTabs.UPLOAD_DATE,
    FilterTabs.INVOICE_DATE,
    FilterTabs.DRAFT_DUE_DATE,
    FilterTabs.DRAFT_COUNTERPARTY,
    FilterTabs.DRAFT_STATUS,
];

const ACCRUAL_DRAFT_SOURCE_OPTIONS = [
    { value: "upload", label: "Upload" },
    { value: "email", label: "Email" },
    { value: DocumentSource.RAMP, label: "Ramp" },
    { value: DocumentSource.MERCURY, label: "Mercury" },
    { value: DocumentSource.PLAID, label: "Plaid" },
    { value: DocumentSource.QUICKBOOKS, label: "QuickBooks" },
].map((option) => ({
    ...option,
    searchableLabel: option.label.toLowerCase(),
}));

const IDLE_INBOX_REFETCH_INTERVAL_MS = 30_000;

const sizeSuffix = `of size less than ${MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB} MB are allowed.`;
const errorMessage = `Only ${ACCRUAL_DOCUMENT_FILE_TYPES_NAMES} ${sizeSuffix}`;

interface Props {
    onHide: () => void;
}

export const AccrualImporterDraftsView: React.FC<Props> = ({ onHide }) => {
    const {
        mode,
        importerQueue,
        documentRows,
        upsertDocumentRow,
        removeDocumentRow,
        isRefreshEnabled,
        sortedRows,
        previewedRowId,
    } = useContext(AccrualImporterContext);
    const isDraftOpen = sortedRows.some((row) => row.id === previewedRowId);
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<FiltersTypes>({});
    const handleFiltersChange = useCallback<FiltersChangeFn>((payload) => {
        setFilters((current) => ({ ...current, ...payload }));
    }, []);
    const [selectedRows, setSelectedRows] = useState<AccrualImporterViewRow[]>(
        [],
    );
    const [selectionResetKey, setSelectionResetKey] = useState(0);
    const handleSelectedRowsChange = useCallback(
        (rows: AccrualImporterViewRow[]) => {
            setSelectedRows(rows);
        },
        [],
    );
    const [visibleRows, setVisibleRows] = useState<
        AccrualImporterDocumentRow[]
    >([]);
    const navigationRows = useMemo(
        () => visibleRows.filter((row) => row.financialDocument),
        [visibleRows],
    );
    const pageView = useAccrualImporterPageView(mode);
    const isInvoices = mode === "invoices";
    const groupByItems = useMemo(
        () => [
            {
                value: AccrualImporterGroupBy.DOCUMENT,
                label: isInvoices ? "By invoice" : "By bill",
            },
            {
                value: AccrualImporterGroupBy.LINE_ITEM,
                label: "By line item",
            },
        ],
        [isInvoices],
    );
    const draftStatusOptions = useMemo(() => {
        const { reviewStatusLabels } = accrualDraftCopy(mode);
        return Object.values(AccrualReviewStatus).map((value) => ({
            value,
            label: reviewStatusLabels[value],
            searchableLabel: reviewStatusLabels[value].toLowerCase(),
        }));
    }, [mode]);
    const draftSourceOptions = useMemo(() => {
        const sourceKeys = new Set(
            documentRows.map((row) =>
                accrualDraftSourceKey(row.financialDocument?.source),
            ),
        );
        return ACCRUAL_DRAFT_SOURCE_OPTIONS.filter((option) =>
            sourceKeys.has(option.value),
        );
    }, [documentRows]);
    const tabLabels = useMemo(
        () => ({
            [FilterTabs.UPLOAD_DATE]: "Date imported",
            [FilterTabs.INVOICE_DATE]: isInvoices
                ? "Invoice date"
                : "Bill date",
            [FilterTabs.DRAFT_COUNTERPARTY]: isInvoices ? "Customer" : "Vendor",
        }),
        [isInvoices],
    );
    const toaster = useToaster();
    const { workspaceId: activeWorkspaceId } = useScope();
    const pendingImportDocumentIdsRef = useRef<Set<number>>(new Set());
    const notifiedFailureDocumentIdsRef = useRef<Set<number>>(new Set());

    const upload = useAccrualImporterUploadMutation({
        onSuccess: () => {
            toaster.toast(<ImporterToast subject={mode} />, IMPORTER_TOAST_KEY);
        },
        onUploadError: (file) => {
            toaster.toast(`Failed to upload ${file.name}`);
        },
    });

    const rowsReadyForImport = useMemo(
        () => documentRows.filter(isAccrualRowReadyForImport),
        [documentRows],
    );

    const hasImportsRunning = useMemo(
        () =>
            documentRows.some(
                (row) =>
                    row.financialDocument &&
                    isImportRunning(row.financialDocument),
            ),
        [documentRows],
    );

    const canCloseImporter = useMemo(
        () =>
            documentRows.length > 0 &&
            documentRows.every(
                (row) =>
                    row.financialDocument &&
                    !accrualDocumentBeingProcessed(row.financialDocument),
            ),
        [documentRows],
    );

    const handleDocumentsRemovedFromImporter = useCallback(
        (importerDocuments: FetchFinancialDocumentsResponse) => {
            const returnedDocumentIds = importerDocuments.data.map(
                (document) => document.id,
            );
            const documentRowsToClear = documentRows.filter(
                (row) =>
                    row.financialDocument &&
                    !returnedDocumentIds.includes(row.financialDocument.id),
            );

            let accrualListsRefreshRequired = false;

            documentRowsToClear.forEach((row) => {
                const financialDocument = row.financialDocument;
                if (!financialDocument) {
                    return;
                }

                const documentId = financialDocument.id;
                const importStatus = financialDocument.transactionsImportStatus;

                if (importStatus === TransactionsImportStatus.FAILED) {
                    return;
                }

                pendingImportDocumentIdsRef.current.delete(documentId);
                notifiedFailureDocumentIdsRef.current.delete(documentId);

                if (
                    importStatus === TransactionsImportStatus.FINISHED ||
                    importStatus === TransactionsImportStatus.IN_PROGRESS ||
                    importStatus === TransactionsImportStatus.SCHEDULED
                ) {
                    upsertDocumentRow({
                        ...row,
                        financialDocument: {
                            ...financialDocument,
                            transactionsImportStatus:
                                TransactionsImportStatus.FINISHED,
                        },
                    });
                    if (importStatus !== TransactionsImportStatus.FINISHED) {
                        accrualListsRefreshRequired = true;
                    }
                } else {
                    removeDocumentRow(row);
                }
            });

            if (accrualListsRefreshRequired) {
                if (mode === "invoices") {
                    invalidateInvoiceQueries();
                } else {
                    invalidateBillQueries();
                }
            }
        },
        [documentRows, mode, removeDocumentRow, upsertDocumentRow],
    );

    const shouldPollImporterDocuments = useCallback(
        (data: FetchFinancialDocumentsResponse | undefined) => {
            if (!isRefreshEnabled) {
                return false;
            }

            const hasProcessingDocumentRow = documentRows.some(
                (row) =>
                    row.financialDocument &&
                    accrualDocumentBeingProcessed(row.financialDocument),
            );

            const hasProcessingServerDocument = (data?.data ?? []).some(
                accrualDocumentBeingProcessed,
            );

            const hasRunningImportServerDocument = (data?.data ?? []).some(
                isImportRunning,
            );

            return (
                hasProcessingDocumentRow ||
                hasProcessingServerDocument ||
                hasImportsRunning ||
                hasRunningImportServerDocument
            );
        },
        [documentRows, hasImportsRunning, isRefreshEnabled],
    );

    const { data: importerDocuments } = useFinancialDocumentsExtendedQuery({
        queryParams: {
            workspaceId: activeWorkspaceId ?? "",
            filters: { importerQueue },
            limit: 0,
            page: 0,
        },
        detailIncludes: accrualImporterDetailIncludes,
        additionalParams: { enabled: !!activeWorkspaceId },
        onSuccess: handleDocumentsRemovedFromImporter,
        shouldRefetch: shouldPollImporterDocuments,
        idleRefetchIntervalMs: isRefreshEnabled
            ? IDLE_INBOX_REFETCH_INTERVAL_MS
            : undefined,
    });

    const importerDocumentData = importerDocuments?.data;

    useEffect(() => {
        importerDocumentData?.forEach((document) => {
            upsertDocumentRow({
                id: document.id.toString(),
                financialDocument: document,
            });
        });
    }, [importerDocumentData, upsertDocumentRow]);

    useEffect(() => {
        for (const row of documentRows) {
            const financialDocument = row.financialDocument;
            if (!financialDocument) {
                continue;
            }

            if (
                financialDocument.transactionsImportStatus !==
                TransactionsImportStatus.FAILED
            ) {
                continue;
            }

            if (
                !pendingImportDocumentIdsRef.current.has(financialDocument.id)
            ) {
                continue;
            }

            if (
                notifiedFailureDocumentIdsRef.current.has(financialDocument.id)
            ) {
                continue;
            }

            notifiedFailureDocumentIdsRef.current.add(financialDocument.id);
            pendingImportDocumentIdsRef.current.delete(financialDocument.id);
            toaster.toast(
                `Failed to import ${financialDocument.fileName}. Review and try again.`,
            );
        }
    }, [documentRows, toaster]);

    const handleImportStarted = useCallback(
        (financialDocumentIds: number[]) => {
            financialDocumentIds.forEach((documentId) => {
                pendingImportDocumentIdsRef.current.add(documentId);
                notifiedFailureDocumentIdsRef.current.delete(documentId);

                const row = documentRows.find(
                    (documentRow) =>
                        documentRow.financialDocument?.id === documentId,
                );
                if (row?.financialDocument) {
                    upsertDocumentRow({
                        ...row,
                        financialDocument: {
                            ...row.financialDocument,
                            transactionsImportStatus:
                                TransactionsImportStatus.SCHEDULED,
                        },
                    });
                }
            });
            toaster.toast(
                <ImporterToast subject={mode} verb="Creating" />,
                IMPORTER_TOAST_KEY,
            );
            setSelectionResetKey((key) => key + 1);
        },
        [documentRows, mode, toaster, upsertDocumentRow],
    );

    const handleImportError = useCallback(
        (financialDocumentIds: number[], error: Error) => {
            financialDocumentIds.forEach((documentId) => {
                pendingImportDocumentIdsRef.current.delete(documentId);
            });
            toaster.toast(`Failed to start import: ${error.message}`);
        },
        [toaster],
    );

    const { confirmDocuments, isConfirming, inFlightDocumentIds } =
        useConfirmAccrualDocuments({
            onImportStarted: handleImportStarted,
            onImportError: handleImportError,
        });
    const confirmDocument = useCallback(
        (financialDocumentId: number) =>
            confirmDocuments([financialDocumentId]),
        [confirmDocuments],
    );

    const onDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            for (const file of acceptedFiles) {
                const id = uuidv7();
                upsertDocumentRow({
                    id,
                    file,
                });
                upload.mutate({
                    file,
                    documentId: id,
                });
            }

            if (fileRejections.length === 1) {
                toaster.toast(errorMessage);
            }

            if (fileRejections.length > 1) {
                toaster.toast(
                    `${fileRejections.length} files rejected. ${errorMessage}`,
                );
            }
        },
        [upsertDocumentRow, toaster, upload],
    );

    const acceptedFileTypes = useMemo(
        () => formatExtensionsForDropzone(ACCRUAL_DOCUMENT_FILE_TYPES),
        [],
    );

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        open: openUploadPrompt,
    } = useDropzone({
        onDrop,
        accept: acceptedFileTypes,
        maxSize: MAX_FINANCIAL_DOCUMENT_FILE_SIZE_BYTES,
        noClick: true,
        noKeyboard: true,
    });

    const dropLabel =
        mode === "invoices"
            ? "Drop invoices to upload"
            : "Drop bills to upload";
    const [isForwardOpen, setIsForwardOpen] = useState(false);

    return (
        <>
            <div className={styles.page}>
                <TablePageHeader
                    search={search}
                    onSearchChange={setSearch}
                    isFullPage
                    borderBottom
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    availableFilters={ACCRUAL_DRAFT_FILTERS}
                    tabLabels={tabLabels}
                    draftStatusOptions={draftStatusOptions}
                    draftSourceOptions={draftSourceOptions}
                >
                    <Picker.Select
                        items={groupByItems}
                        value={pageView.groupBy}
                        onValueChange={(value) => {
                            if (value) {
                                pageView.setGroupBy(value);
                            }
                        }}
                        trigger={<Picker.Button variant="primary" size="md" />}
                    />
                    <EntityFilterCombobox
                        filters={{ entityIds: filters.entityIds }}
                        onChange={(next) =>
                            handleFiltersChange({ entityIds: next.entityIds })
                        }
                        onlyBusiness
                        excludeAllMockEntities
                        showLabel={false}
                    />
                    <AccrualDraftCreateMenu
                        mode={mode}
                        onUpload={openUploadPrompt}
                        onForwardViaEmail={() => setIsForwardOpen(true)}
                    />
                </TablePageHeader>
                <FileDropzone
                    rootProps={getRootProps()}
                    isDragActive={isDragActive}
                    className={styles.dropzone}
                    coverClassName={styles.cover}
                    cover={
                        <div className={styles.dropCover}>
                            <UploadIcon />
                            <span>{dropLabel}</span>
                        </div>
                    }
                >
                    <div className={styles.tableScroll}>
                        <VStack
                            gap={16}
                            className={classNames("p-0", styles.tableColumn)}
                        >
                            <AccrualImporterDraftsTable
                                search={search}
                                filters={filters}
                                pageView={pageView}
                                onSelectedRowsChange={handleSelectedRowsChange}
                                onVisibleRowsChange={setVisibleRows}
                                selectionResetKey={selectionResetKey}
                                onConfirmDocument={confirmDocument}
                                onConfirmDocuments={confirmDocuments}
                                confirmingDocumentIds={inFlightDocumentIds}
                                reviewAction={
                                    isDraftOpen ? null : (
                                        <AccrualImporterReviewButton
                                            rowsReadyForImport={
                                                rowsReadyForImport
                                            }
                                            selectedRows={selectedRows}
                                            confirmDocuments={confirmDocuments}
                                            isConfirming={isConfirming}
                                        />
                                    )
                                }
                            />
                        </VStack>
                    </div>
                    <input {...getInputProps()} />
                </FileDropzone>
                {selectedRows.length === 0 && !isDraftOpen && (
                    <AccrualImporterPageFooter
                        rowsReadyForImport={rowsReadyForImport}
                        hasImportsRunning={hasImportsRunning}
                        canClose={canCloseImporter}
                        onHide={onHide}
                        openUploadPrompt={openUploadPrompt}
                        confirmDocuments={confirmDocuments}
                        isConfirming={isConfirming}
                    />
                )}
            </div>
            <AccrualDraftDetailsDrawer navigationRows={navigationRows} />
            <ForwardViaEmailModal
                show={isForwardOpen}
                mode={mode}
                onClose={() => setIsForwardOpen(false)}
            />
        </>
    );
};
